//import { Platform } from 'react-native'
import React, { useState, useMemo } from 'react'
import { useAuthedRequestFn, formatDate, color, FormatUtils, safeBigNum } from '@my/util'
import {
  FinancialTable,
  XStack,
  YStack,
  ActionButton,
  SimpleText,
  useToastController,
  TextInputComponent,
  CustomDropdown,
  ScrollView,
  Spinner,
  XPPlatform as Platform,
  Button,
  TZASummaryCards,
  DropDownPickerView,
  ActiveButton,
  ActiveDropdown
} from '@my/ui'
import { useForm, Controller } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { FinancialGoalChart } from './financialGoalChart/FinancialGoalChart'
import {
  FinancialGoalsModal,
  FinancialReviewHeading,
  ConfirmationModal,
} from '../../../components/index'
import { FinancialGoalSummary } from './financialGoalSummary'
import images from '../../../assets/images'
import { FinancialGoalsUtils } from '../../../utils/financialGoals'
import { orderBy } from 'lodash'
import { DollarSign, TrendingUp, MoveRight, Gauge, Goal, CheckCheck, Plus } from '@tamagui/lucide-icons-2'
import { InputField } from '../../financial-profile/helpers/Helper'

// Constants
const PRIORITIES = ['High', 'Medium', 'Low']
const DEFAULT_PRIORITY_ITEMS = [
  { label: 'All', value: 'All' },
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' },
]
const DEFAULT_GOAL_ITEMS = [
  { label: 'All', value: 'All' },
  { label: 'Completed', value: 'completed' },
  { label: 'Pending', value: 'Pending' },
]
const DEFAULT_TABLE_HEADINGS = [
  { key: 'label', text: 'Completed' },
  { key: 'name', text: 'Goal Name' },
  { key: 'amount', text: 'Target Amount' },
  { key: 'RemainingAmount', text: 'Remaining Amount' },
  { key: 'timeline', text: 'Timeline' },
  { key: 'potential', text: 'Success Probability', info: true, infoText: "Your likelihood of hitting this goal across thousands of 'what if' scenarios factoring in market projections, savings rate, and timeline. Lower score? No stress - small tweaks can make a big difference." },
  { key: 'priority', text: 'Priority' },
  { key: 'actionButtons', text: 'Actions' },
  //...Platform.select({ web: [{ key: 'actionButtons', text: 'Actions' }], native: []}),
  { key: 'expander', text: '' },
]

// Helper to create goal payload
export const createGoalPayload = (data: any, goalData: any, goalTypeFromDate: string) => ({
  goalName: data?.goalName,
  category: goalData.goalType || goalTypeFromDate,
  targetAmount: data?.targetAmount,
  targetDate: data?.timelineTimestamp,
  priority: data?.priority,
  description: data?.description,
})

const FilterDropdown = ActiveDropdown;

const FinancialGoalTableHeader = ({ priority, setPriority, filter, setFilter, addGoalButton }) => {
  return (
    <XStack justifyContent="space-between" width="100%" alignItems="center" $max-sm={{ flexWrap:'wrap', rowGap:15 }}>
      <SimpleText fontSize={14} fontWeight={'800'} color={color.grey} lineHeight={17} p={3}>
        GOALS TABLE
      </SimpleText>
      <XStack 
        style={{
          alignItems: "center", flexWrap: 'wrap', gap: 5, rowGap: 15, marginLeft: 'auto'
        }}
        $max-sm={{width: '100%'}}
      >
        {Platform.select({ native: null, web: <>
          <XStack>
            <SimpleText>Priority:</SimpleText>
          </XStack>
          <FilterDropdown
            items={DEFAULT_PRIORITY_ITEMS}
            val={priority}
            setVal={setPriority}
            placeholder={'Priority'}
            containerStyle={[{ width: 96 }]}
          />
          <XStack>
            <SimpleText>Status:</SimpleText>
          </XStack>
          <FilterDropdown
            items={DEFAULT_GOAL_ITEMS}
            val={filter}
            setVal={setFilter}
            placeholder={'All'}
            containerStyle={[{ width: 112 }]}
          />
        </> })}
        {addGoalButton}
      </XStack>
    </XStack>
  )
}
// FinancialGoal component
export const FinancialGoals = () => {
  const today = new Date()
  // State management
  const [priority, setPriority] = useState('')
  const [filter, setFilter] = useState(null)
  const [goalData, setGoalData] = useState({
    goalId: '',
    goalType: '',
    goalModal: false,
    deletePrompt: false,
  })

  const toast = useToastController()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      goalName: '',
      targetAmount: '',
      timelineTimestamp: new Date(today.getFullYear() + 1, today.getMonth(), today.getDate() + 1),
      priority: '',
      description: '',
    },
  })

  // Fetch data
  const { data, isLoading } = useQuery({
    queryKey: ['financial-goals', filter, priority],
    queryFn: () => authedRequestFn(`/financial-goals?status=${filter}&priority=${priority}`),
  })
  // Destructure with default empty object
  const {
    completedGoals = 0,
    financialGoals = [],
    goalProgressBreakdown = [],
    overallProgress = 0,
    totalGoals = 0,
    totalTargetAmount,
  } = data || {}

  const resetGoalData = () => {
    reset()
    setGoalData({
      goalId: '',
      goalType: '',
      goalModal: false,
      deletePrompt: false,
    })
  }

  const postFinancialGoal = useMutation({
    mutationFn: (formData: any) =>
      authedRequestFn('/financial-goals', { method: 'POST', body: formData }),
    onSuccess: () => {
      reset()
      resetGoalData()
      queryClient.invalidateQueries({ queryKey: ['financial-goals'] })
      toast.show('Successfully Saved!')
    },
    onError: () => {
      resetGoalData()
    },
  })

  const updateFinancialGoal = useMutation({
    mutationFn: ({payload, isCompletionUpdate}: { payload: any, isCompletionUpdate?: boolean}) =>
      authedRequestFn(`/financial-goals/${payload.goalId || goalData.goalId}`, {
        method: 'PUT',
        body: payload,
        query: isCompletionUpdate ? { isCompletionUpdate } : {},
      }),
    onSuccess: () => {
      resetGoalData()
      queryClient.invalidateQueries({ queryKey: ['financial-goals'] })
      toast.show('Goal Updated!')
    },
    onError: () => {
      resetGoalData()
    },
  })
  const deleteFinancialGoal = useMutation({
    mutationFn: (payload: any) =>
      authedRequestFn(`/financial-goals/${payload.goalId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      resetGoalData()
      queryClient.invalidateQueries({ queryKey: ['financial-goals'] })
      toast.show('Goal Deleted!')
    },
    onError: () => {
      resetGoalData()
    },
  })

  // Handle goal submission
  const handleSubmitForm: any = (data: any) => {
    try {
      const goalTypeFromDate = FinancialGoalsUtils.goalCategoryFromTargetDate(data?.timelineTimestamp)
      const payload = createGoalPayload(data, goalData, goalTypeFromDate)
      if (goalData?.goalId) {
        updateFinancialGoal.mutate({payload})
      } else {
        postFinancialGoal.mutate(payload)
      }
      setGoalData((prevState) => ({ ...prevState, goalModal: false }))
    } catch (error) {
      console.error('Error submitting form:', error)
    }
  }

  // Goals mapping logic
  const goalsArray = useMemo(() => {
    const sortedGoals = orderBy(
      financialGoals || [], 
      [goal => goal.priority === 'Low' ? 0 : goal.priority === 'High' ? 2 : 1, 'targetDate', 'successPotential'], 
      ['desc', 'asc', 'desc']
    );
    return sortedGoals.map((goal) => {
      const succPotentialBn = safeBigNum(goal.successPotential);
      const targetAmountBn = safeBigNum(goal.targetAmount);
      return {
        label: {
          checkbox: {
            value: goal.completed === true,
            onChange: async (checked: boolean) => {
              const payload = {...goal, completed: checked };
              delete payload['_id'];
              delete payload['id'];
              return updateFinancialGoal.mutate({payload, isCompletionUpdate: true})
            }
          }
        },
        name: goal.goalName,
        amount: `${FormatUtils.currency.cashFormat(targetAmountBn)}`,
        RemainingAmount: goal.completed ? '$0' : `${FormatUtils.currency.cashFormat(targetAmountBn.minus(safeBigNum(goal?.currentAmount)))}`,
        timeline: FormatUtils.dateTime['Month yyyy'](goal.targetDate),
        potential: goal.completed ? '100%' : succPotentialBn.isNaN() ? '- -' : succPotentialBn.toFixed(2) + '%',
        priority: {
          priorityBadge: true,
          priority: goal.priority
        },
        actionButtons: {
          edit: () => editGoal(goal),
          delete: () => deleteGoal(goal),
        },
        /* expander: {
          expander: {
            renderFn: ({ onClick, isExpanded }) => {
              return <Button
                {...{
                  onClick,
                  style: { cursor: 'pointer' },
                }}
              >
                {isExpanded ? '👇' : '👉'}
              </Button>
            }
          }
        } */
      }
    })
  }, [financialGoals])

  const editGoal = (goal: any) => {
    setValue('description', goal.description)
    setValue('goalName', goal?.goalName)
    setValue('priority', goal?.priority)
    setValue('targetAmount', goal?.targetAmount.toString())
    setValue('timelineTimestamp', new Date(goal?.targetDate))
    setGoalData((prevState) => ({
      ...prevState,
      goalId: goal?.goalId,
      goalType: goal.category,
      deletePrompt: false,
      goalModal: true,
    }))
  }

  const deleteGoal = (goal: any) => {
    setGoalData({
      goalId: goal?._id,
      goalType: goal?.category,
      goalModal: false,
      deletePrompt: true,
    })
  }

  const AddGoalButton = () => (
      <ActiveButton 
        style={{width: 150, height: 44, ...Platform.select({web: {}, native:{marginLeft: 'auto'}})}} ButtonTextProps={{children: '+ Add Goal'}} 
        onPress={() => setGoalData((prevState) => ({ ...prevState, goalModal: true }))}
      />
      /* <ActionButton
        onPress={() => setGoalData((prevState) => ({ ...prevState, goalModal: true }))}
        color="#ffffffff"
        borderRadius={9}
        backgroundColor={color.tzaGreen}
        //fontWeight="600"
        height={44}
        fontSize={15}
        icon={Plus}
        $sm={{
          margin:'auto'
        }}
      >
        Add Goal
      </ActionButton> */
  )
  // Render text input components with validation
  const renderTextInput = (
    name: keyof typeof getValues,
    placeholder: string,
    rules?: any,
    props?: any
  ) => {
    return (
      <InputField
        control={control}
        name={name}
        rules={rules}
        placeholder={placeholder}
        showPlaceholder
        disableLabel
      />
    )
  }
  // Render priority buttons for selecting goal priority
  const renderPriorityButtons = () => (
    <Controller
      control={control}
      name="priority"
      rules={{ required: 'Priority level is required' }}
      render={({ field: { onChange, value } }) => (
        <XStack marginVertical={10} gap={10}>
          {PRIORITIES.map((item) => (
            <ActiveButton
              key={item}
              //unstyled
              style={{ width: 88, height: 34, padding: 0}}
              variant={value === item ? undefined : 'outlined'}
              onPress={() => onChange(item)}
              ButtonTextProps={{children: item}}
            />
          ))}
        </XStack>
      )}
    />
  )

  // Main Render
  const RenderFinancialGoalScreen = ({ goalsArray }: { goalsArray: any }) => (
    <YStack p="$4" flex={1} height="auto" backgroundColor="white" gap={25}>
      <FinancialReviewHeading
        title="Financial Goals"
        img={images.financialGoals}
        imageStyle={{
          '$platform-native': { width: 26, height: 22 },
          '$platform-web': { width: 34, height: 29 },
        }}
      />
      
    </YStack>
  )

  const mainLayout = <>
    <TZASummaryCards 
      cards={[
        { title: "Total Goals", value:totalGoals, icon: <Goal color='rgb(16, 185, 129)'/> },
        { title: "Completed Goals", value: completedGoals, icon: <CheckCheck color='rgb(59, 130, 246)' /> },
        { title: "Overall Progress", value: `${overallProgress.toFixed(0)}%`, icon: <Gauge color='rgb(168, 85, 247)' /> }
      ]}
    />
    {totalGoals > 0 && (
      <FinancialGoalChart
        overallGoalCompletionPercentage={overallProgress}
        goals={goalProgressBreakdown}
        totalTargetAmount={totalTargetAmount}
      />
    )}
    <FinancialGoalTableHeader
      priority={priority}
      setPriority={setPriority}
      filter={filter}
      setFilter={setFilter}
      addGoalButton={<AddGoalButton />}
    />
    
    <FinancialTable 
      data={goalsArray} 
      headings={DEFAULT_TABLE_HEADINGS} 
      mobileRenderOptions={{
        titleKey: 'name',
        mainValueKeys: ['label', 'amount', ],
        interactiveKeys: ['actionButtons'],
        otherKeys: [['priority', 'potential'], ['RemainingAmount', 'timeline' ]]
      }}
      /* expandProps={{
        getRowCanExpand: (row) => { console.log({row}); return true},
        renderSubComponent: () => <Button>'Hi!!!'</Button>
      }} */
    />
    
  </>

  const output = <YStack p="$4" bg={color.white} gap={25}>
    {/* Heading Section */}
    <FinancialReviewHeading
      title="Financial Goals"
      img={images.financialGoals}
      imageStyle={{
        //'$platform-web': { width: 20, height: 28 },
      }}
    />
    {isLoading 
      ? <Spinner size="large" color={color.tzaGreen} />
      : mainLayout
    }
  </YStack>

  return (
    <>
      <>
        <FinancialGoalsModal
          modalVisible={goalData.goalModal}
          control={control}
          handleSubmit={handleSubmit}
          renderTextInput={renderTextInput}
          renderPriorityButtons={renderPriorityButtons}
          onClose={resetGoalData}
          handleSubmitForm={handleSubmitForm}
          goalType={goalData.goalType}
        />
        <ConfirmationModal
          modal={goalData.deletePrompt}
          text={'delete this goal'}
          onConfirm={() => deleteFinancialGoal.mutate(goalData)}
          onClose={resetGoalData}
        />
      </>
      {Platform.OS === 'web' ? (
        output
      ) : (
        <ScrollView backgroundColor={color.white}>
          {output}
        </ScrollView>
      )}
    </>
  )
}
